import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useOnTimeLiveData } from '../hooks/useOnTimeLiveData';
import StatusPill from '../components/StatusPill';
import {
  copyStoreHandoffCardImage,
  downloadStoreHandoffCard,
  openWhatsAppWeb,
  previewStoreHandoffCard,
} from '../lib/storeHandoffCard';
import { asDate, formatMoney } from '../lib/formatters';
import { loadOnTimeSettings } from '../lib/ontimeSettings';

function money(value) {
  return formatMoney(Number(value || 0));
}

function lineTotal(item) {
  return Number(item?.lineTotal || 0);
}

function sentToStoreDate(order) {
  return asDate(
    order?.timings?.sentToStoreAt ||
      order?.raw?.timings?.sentToStoreAt ||
      order?.raw?.sentToStoreAt ||
      order?.raw?.storeQueuedAt ||
      order?.raw?.updatedAt ||
      order?.placedAt ||
      order?.createdAt
  );
}

function elapsedLabel(from, nowMs) {
  const dt = asDate(from);
  if (!dt) return '—';
  const totalSeconds = Math.max(0, Math.floor((Number(nowMs || Date.now()) - dt.getTime()) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}

function minutesWaiting(from, nowMs) {
  const dt = asDate(from);
  if (!dt) return 0;
  return Math.max(0, Math.floor((Number(nowMs || Date.now()) - dt.getTime()) / 60000));
}

function timerTone(mins, hasIssue) {
  if (hasIssue || mins >= 45) return 'border-rose-400/45 bg-rose-500/12 text-rose-100 shadow-[0_0_24px_rgba(244,63,94,.18)]';
  if (mins >= 20) return 'border-amber-300/45 bg-amber-400/12 text-amber-100 shadow-[0_0_22px_rgba(251,191,36,.16)]';
  return 'border-emerald-300/35 bg-emerald-400/10 text-emerald-100 shadow-[0_0_22px_rgba(52,211,153,.12)]';
}

function storeIssueCount(orders) {
  return orders.filter((order) => order.issueFlag || order.stage === 'issue').length;
}

function stageLabel(stage) {
  if (stage === 'awaiting_store') return 'Waiting store';
  if (stage === 'ready_for_driver') return 'Ready driver';
  if (stage === 'issue') return 'Issue';
  return String(stage || 'Live').replace(/_/g, ' ');
}

export default function OnTimeStoreQueuePage() {
  const { claims } = useAuth();
  const { orders } = useOnTimeLiveData();
  const [busyId, setBusyId] = useState('');
  const [message, setMessage] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewOrder, setPreviewOrder] = useState('');
  const [cardTheme, setCardTheme] = useState('dark');
  const [openStores, setOpenStores] = useState({});
  const [nowMs, setNowMs] = useState(Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!claims?.orgId) return;
    loadOnTimeSettings(claims.orgId)
      .then((settings) => setCardTheme(settings?.storeHandoffCardTheme === 'light' ? 'light' : 'dark'))
      .catch(() => setCardTheme('dark'));
  }, [claims?.orgId]);

  const storeOrders = useMemo(
    () => orders.filter((order) => ['awaiting_store', 'ready_for_driver', 'issue'].includes(order.stage)),
    [orders]
  );

  const groupedStores = useMemo(() => {
    const groups = new Map();
    storeOrders.forEach((order) => {
      const key = order.store || 'Unassigned store';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(order);
    });

    return Array.from(groups.entries())
      .map(([store, list]) => {
        const sorted = [...list].sort((a, b) => (sentToStoreDate(a)?.getTime() || 0) - (sentToStoreDate(b)?.getTime() || 0));
        const longestOrder = sorted.reduce((oldest, order) => {
          const oldestTime = sentToStoreDate(oldest)?.getTime() || Infinity;
          const orderTime = sentToStoreDate(order)?.getTime() || Infinity;
          return orderTime < oldestTime ? order : oldest;
        }, sorted[0]);
        const longestDate = sentToStoreDate(longestOrder);
        const longestMinutes = minutesWaiting(longestDate, nowMs);
        const issueCount = storeIssueCount(sorted);
        const readyCount = sorted.filter((order) => order.stage === 'ready_for_driver').length;
        const awaitingCount = sorted.filter((order) => order.stage === 'awaiting_store').length;
        const totalValue = sorted.reduce((sum, order) => sum + Number(order.total || 0), 0);
        return { store, orders: sorted, longestOrder, longestDate, longestMinutes, issueCount, readyCount, awaitingCount, totalValue };
      })
      .sort((a, b) => b.longestMinutes - a.longestMinutes || a.store.localeCompare(b.store));
  }, [storeOrders, nowMs]);

  async function handleCard(action, order) {
    setBusyId(`${order.id}:${action}`);
    setMessage('');
    try {
      if (action === 'copy') {
        await copyStoreHandoffCardImage(order, { theme: cardTheme });
        setMessage(`PNG card copied. Open WhatsApp Web and paste for ${order.orderNumber || order.id}.`);
      }
      if (action === 'download') {
        await downloadStoreHandoffCard(order, { theme: cardTheme });
        setMessage(`PNG card downloaded for ${order.orderNumber || order.id}.`);
      }
      if (action === 'preview') {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        const url = await previewStoreHandoffCard(order, { theme: cardTheme });
        setPreviewUrl(url);
        setPreviewOrder(order.orderNumber || order.id);
      }
      if (action === 'whatsapp') {
        openWhatsAppWeb();
        setMessage('WhatsApp Web opened. Copy or download the PNG card, then paste it into the store group.');
      }
    } catch (error) {
      setMessage(error?.message || 'Card action failed.');
    } finally {
      setBusyId('');
    }
  }

  function toggleStore(store) {
    setOpenStores((prev) => ({ ...prev, [store]: !prev[store] }));
  }

  return (
    <div className="space-y-6 pb-8">
      <div className="rounded-[2rem] border border-cyan-300/15 bg-[linear-gradient(135deg,rgba(8,28,48,.88),rgba(8,12,28,.72))] p-5 shadow-[0_0_45px_rgba(34,211,238,.08)]">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.34em] text-cyan-300">Store command</div>
            <h2 className="mt-2 text-3xl font-black text-white">Store Queue</h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-300">
              Online team handoff board grouped by store. Expand only when you need to copy cards, review packing detail, or inspect issues.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center text-xs sm:min-w-[420px]">
            <div className="rounded-2xl border border-cyan-300/15 bg-cyan-400/8 px-3 py-3">
              <div className="text-2xl font-black text-white">{storeOrders.length}</div>
              <div className="mt-1 uppercase tracking-[0.18em] text-cyan-200">orders</div>
            </div>
            <div className="rounded-2xl border border-amber-300/15 bg-amber-400/8 px-3 py-3">
              <div className="text-2xl font-black text-white">{groupedStores.reduce((sum, group) => sum + group.issueCount, 0)}</div>
              <div className="mt-1 uppercase tracking-[0.18em] text-amber-200">issues</div>
            </div>
            <div className="rounded-2xl border border-emerald-300/15 bg-emerald-400/8 px-3 py-3">
              <div className="text-2xl font-black text-white">{groupedStores.length}</div>
              <div className="mt-1 uppercase tracking-[0.18em] text-emerald-200">stores</div>
            </div>
          </div>
        </div>
        <div className="mt-4 text-xs text-cyan-300/90">Current card theme: {cardTheme === 'light' ? 'Light' : 'Dark'} · change it in Settings.</div>
      </div>

      {message ? (
        <div className="rounded-2xl border border-cyan-400/20 bg-cyan-500/10 px-4 py-3 text-sm text-cyan-100 shadow-[0_0_22px_rgba(34,211,238,.12)]">{message}</div>
      ) : null}

      {previewUrl ? (
        <div className="rounded-[2rem] border border-cyan-300/15 bg-[#081426]/90 p-4 shadow-[0_0_40px_rgba(34,211,238,.12)]">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.24em] text-cyan-300">PNG preview</div>
              <div className="text-white">{previewOrder}</div>
            </div>
            <button
              onClick={() => {
                if (previewUrl) URL.revokeObjectURL(previewUrl);
                setPreviewUrl('');
                setPreviewOrder('');
              }}
              className="rounded-2xl border border-white/10 px-3 py-2 text-sm text-slate-300 transition hover:border-cyan-300/30 hover:text-cyan-100"
            >
              Close preview
            </button>
          </div>
          <img src={previewUrl} alt="Store handoff card preview" className="mx-auto w-full max-w-[460px] rounded-3xl border border-white/10 shadow-2xl" />
        </div>
      ) : null}

      <div className="space-y-4">
        {groupedStores.length === 0 ? (
          <div className="rounded-[2rem] border border-cyan-300/15 bg-[#081426]/80 p-8 text-center text-slate-300">No orders waiting in store queue.</div>
        ) : null}

        {groupedStores.map((group) => {
          const isOpen = Boolean(openStores[group.store]);
          const tone = timerTone(group.longestMinutes, group.issueCount > 0);
          return (
            <section key={group.store} className="overflow-hidden rounded-[2rem] border border-cyan-300/15 bg-[linear-gradient(145deg,rgba(10,23,42,.92),rgba(5,11,24,.92))] shadow-[0_0_36px_rgba(34,211,238,.08)]">
              <button
                type="button"
                onClick={() => toggleStore(group.store)}
                className="group w-full p-5 text-left transition hover:bg-cyan-300/[0.035] hover:shadow-[inset_0_0_45px_rgba(34,211,238,.08)]"
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex min-w-0 items-center gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-cyan-300/25 bg-cyan-400/10 text-lg font-black text-cyan-100 shadow-[0_0_24px_rgba(34,211,238,.16)]">
                      {isOpen ? '−' : '+'}
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-[0.32em] text-cyan-300">{group.awaitingCount > 0 ? 'Awaiting store' : 'Store handoff'}</div>
                      <h3 className="mt-1 truncate text-2xl font-black text-white">{group.store}</h3>
                      <div className="mt-1 text-sm text-slate-400">
                        Longest waiting: <span className="font-semibold text-cyan-100">{group.longestOrder?.orderNumber || group.longestOrder?.id || '—'}</span>
                        {group.issueCount > 0 ? <span className="ml-2 text-amber-200">· {group.issueCount} issue flagged</span> : null}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4 lg:min-w-[560px]">
                    <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-3">
                      <div className="text-xl font-black text-white">{group.orders.length}</div>
                      <div className="mt-1 uppercase tracking-[0.2em] text-slate-400">waiting</div>
                    </div>
                    <div className={`rounded-2xl border px-3 py-3 ${tone}`}>
                      <div className="text-xl font-black">{elapsedLabel(group.longestDate, nowMs)}</div>
                      <div className="mt-1 uppercase tracking-[0.2em] opacity-80">longest</div>
                    </div>
                    <div className="rounded-2xl border border-sky-300/15 bg-sky-400/8 px-3 py-3">
                      <div className="text-xl font-black text-white">{group.readyCount}</div>
                      <div className="mt-1 uppercase tracking-[0.2em] text-sky-200">ready</div>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-3">
                      <div className="text-xl font-black text-white">{money(group.totalValue)}</div>
                      <div className="mt-1 uppercase tracking-[0.2em] text-slate-400">value</div>
                    </div>
                  </div>
                </div>
              </button>

              {isOpen ? (
                <div className="border-t border-cyan-300/10 p-4 pt-5">
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-cyan-300/12 bg-cyan-400/[0.045] px-4 py-3 text-sm text-cyan-100">
                    <div>
                      <span className="font-semibold text-white">Online team view:</span> copy/preview store handoff cards, monitor timers, and watch for proof photos as Store + Driver apps come online.
                    </div>
                    <div className="text-xs uppercase tracking-[0.22em] text-cyan-300">Everything LINKed</div>
                  </div>

                  <div className="grid gap-4 xl:grid-cols-2">
                    {group.orders.map((order) => {
                      const sentAt = sentToStoreDate(order);
                      const mins = minutesWaiting(sentAt, nowMs);
                      return (
                        <article key={order.id} className="rounded-[1.6rem] border border-cyan-300/16 bg-[linear-gradient(145deg,rgba(18,38,60,.94),rgba(10,20,36,.96))] p-4 text-slate-100 shadow-[0_0_30px_rgba(34,211,238,.08)] transition hover:border-cyan-300/32 hover:bg-[linear-gradient(145deg,rgba(25,52,78,.96),rgba(11,24,42,.98))] hover:shadow-[0_0_38px_rgba(34,211,238,.16)]">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="text-xs uppercase tracking-[0.24em] text-cyan-300">Store handoff</div>
                              <div className="mt-1 text-2xl font-black text-white">{order.orderNumber || order.id}</div>
                              <div className="mt-1 text-xs text-slate-400">{order.source} · sent {order.timePlaced}</div>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <StatusPill stage={order.stage} />
                              <div className={`rounded-full border px-3 py-1 text-xs font-bold ${timerTone(mins, order.issueFlag || order.stage === 'issue')}`}>
                                {elapsedLabel(sentAt, nowMs)} in store
                              </div>
                            </div>
                          </div>

                          <div className="mt-4 grid gap-3 md:grid-cols-[1.15fr_.85fr]">
                            <section className="rounded-3xl border border-white/8 bg-white/[0.045] p-4">
                              <div className="text-xs uppercase tracking-[0.22em] text-cyan-200/80">Customer</div>
                              <div className="mt-2 text-lg font-bold text-white">{order.customerName}</div>
                              <div className="text-sm font-semibold text-cyan-100">{order.customerPhone || 'No phone saved'}</div>
                              <div className="mt-3 text-sm leading-6 text-slate-200">{order.address || 'No address saved'}</div>
                              {order.notes ? (
                                <div className="mt-3 rounded-2xl border border-cyan-300/12 bg-cyan-400/[0.055] px-3 py-2 text-sm text-slate-100">
                                  Note: {order.notes}
                                </div>
                              ) : null}
                            </section>

                            <section className="rounded-3xl border border-white/8 bg-white/[0.045] p-4">
                              <div className="text-xs uppercase tracking-[0.22em] text-cyan-200/80">Fulfilment</div>
                              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                                <div>
                                  <div className="text-slate-400">Stage</div>
                                  <div className="font-bold capitalize text-white">{stageLabel(order.stage)}</div>
                                </div>
                                <div>
                                  <div className="text-slate-400">Payment</div>
                                  <div className="font-bold capitalize text-white">{order.paymentMethod}</div>
                                </div>
                                <div>
                                  <div className="text-slate-400">Sub-total</div>
                                  <div className="font-bold text-white">{money(order.subtotal)}</div>
                                </div>
                                <div>
                                  <div className="text-slate-400">Total</div>
                                  <div className="font-bold text-white">{money(order.total)}</div>
                                </div>
                              </div>
                              {order.issueFlag ? (
                                <div className="mt-4 rounded-2xl border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">
                                  Stock or customer issue flagged. Check Control Tower before pushing to store.
                                </div>
                              ) : null}
                            </section>
                          </div>

                          <section className="mt-3 rounded-3xl border border-white/8 bg-white/[0.045] p-4">
                            <div className="flex items-center justify-between gap-3">
                              <div className="text-xs uppercase tracking-[0.22em] text-cyan-200/80">Items to pack</div>
                              <div className="text-xs text-slate-400">{order.items.length} line{order.items.length === 1 ? '' : 's'}</div>
                            </div>
                            <div className="mt-3 space-y-2 text-sm">
                              {order.items.map((item) => (
                                <div key={item.lineId || item.name} className="flex items-center justify-between rounded-2xl border border-cyan-300/10 bg-[#0b1b2e]/80 px-4 py-3">
                                  <div className="min-w-0 pr-4 text-white">{item.qty} × {item.name}</div>
                                  <div className="shrink-0 font-bold text-cyan-100">{money(lineTotal(item))}</div>
                                </div>
                              ))}
                            </div>
                          </section>

                          <div className="mt-4 grid grid-cols-2 gap-2 text-sm lg:grid-cols-4">
                            <button
                              onClick={() => handleCard('copy', order)}
                              disabled={busyId === `${order.id}:copy`}
                              className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-emerald-100 transition hover:bg-emerald-400/15 hover:shadow-[0_0_20px_rgba(52,211,153,.16)] disabled:opacity-60"
                            >
                              {busyId === `${order.id}:copy` ? 'Copying…' : 'Copy PNG'}
                            </button>
                            <button
                              onClick={() => handleCard('download', order)}
                              disabled={busyId === `${order.id}:download`}
                              className="rounded-2xl border border-lime-400/30 bg-lime-500/10 px-3 py-2 text-lime-100 transition hover:bg-lime-400/15 hover:shadow-[0_0_20px_rgba(163,230,53,.14)] disabled:opacity-60"
                            >
                              {busyId === `${order.id}:download` ? 'Preparing…' : 'Download'}
                            </button>
                            <button
                              onClick={() => handleCard('whatsapp', order)}
                              className="rounded-2xl border border-cyan-400/30 bg-cyan-500/10 px-3 py-2 text-cyan-100 transition hover:bg-cyan-400/15 hover:shadow-[0_0_20px_rgba(34,211,238,.16)]"
                            >
                              WhatsApp Web
                            </button>
                            <button
                              onClick={() => handleCard('preview', order)}
                              disabled={busyId === `${order.id}:preview`}
                              className="rounded-2xl border border-white/12 bg-white/[0.04] px-3 py-2 text-slate-100 transition hover:border-cyan-300/30 hover:bg-white/[0.07] disabled:opacity-60"
                            >
                              {busyId === `${order.id}:preview` ? 'Rendering…' : 'Preview'}
                            </button>
                          </div>

                          <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-400 md:grid-cols-4">
                            <div className="rounded-2xl border border-white/8 bg-black/15 px-3 py-2">Pack proof: <span className="text-slate-200">coming from Store app</span></div>
                            <div className="rounded-2xl border border-white/8 bg-black/15 px-3 py-2">Handover proof: <span className="text-slate-200">coming from Driver app</span></div>
                            <div className="rounded-2xl border border-white/8 bg-black/15 px-3 py-2">Stock signal: <span className="text-cyan-100">POS-ready</span></div>
                            <div className="rounded-2xl border border-white/8 bg-black/15 px-3 py-2">ETA: <span className="text-slate-200">{order.eta}</span></div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </section>
          );
        })}
      </div>
    </div>
  );
}
